import { buildAppPath } from "@/lib/site-url";
import { PUBLIC_STUDENT_PRIMARY_NAV } from "@/lib/public-student-entry-content";

export const PUBLIC_MARKETING_PATHS = new Set([
  "/",
  "/features",
  "/pricing",
  "/about",
  "/contact",
  "/help/how-to-pay",
  "/install",
  "/install-guide",
  "/privacy",
  "/terms",
  "/refund-policy",
  "/publisher-agreement",
  "/copyright",
  "/toofi",
  "/offline-study",
  "/trusted-resources",
  "/for-publishers",
  "/share/study-plan",
]);

export const PUBLIC_MARKETING_PRIMARY_NAV = PUBLIC_STUDENT_PRIMARY_NAV;

export const PUBLIC_MARKETING_SECONDARY_NAV = [
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
  { label: "Install", href: "/install" },
  { label: "For Publishers", href: "/for-publishers" },
] as const;

export const PUBLIC_FOOTER_GROUPS = [
  {
    title: "Study",
    links: [
      { label: "Features", href: "/features" },
      { label: "Toofi", href: "/toofi" },
      { label: "Offline Study", href: "/offline-study" },
      { label: "Trusted Resources", href: "/trusted-resources" },
      { label: "Pricing", href: "/pricing" },
    ],
  },
  {
    title: "Get Started",
    links: [
      { label: "Start Free", href: "/auth/signup" },
      { label: "Sign In", href: "/auth/login" },
      { label: "Install Guide", href: "/install" },
      { label: "Contact", href: "/contact" },
    ],
  },
  {
    title: "Company and Policy",
    links: [
      { label: "About", href: "/about" },
      { label: "For Publishers", href: "/for-publishers" },
      { label: "Privacy", href: "/privacy" },
      { label: "Terms", href: "/terms" },
      { label: "Refunds", href: "/refund-policy" },
      { label: "Publisher Agreement", href: "/publisher-agreement" },
    ],
  },
] as const;

export const PUBLIC_ATTRIBUTION_KEYS = [
  "source",
  "surface",
  "cta",
  "tier",
  "entry_page",
  "accountType",
  "next",
] as const;

export type PublicAttributionKey = (typeof PUBLIC_ATTRIBUTION_KEYS)[number];

export type PublicAttributionParams = Partial<Record<PublicAttributionKey, string>>;

export const PUBLIC_AUTH_ATTRIBUTION_STORAGE_KEY = "athr:public-auth-attribution";

export const isPublicMarketingPath = (pathname: string) => PUBLIC_MARKETING_PATHS.has(pathname);

export const getPublicSurface = (pathname: string) => {
  const routes: Record<string, string> = {
    "/": "home",
    "/features": "features",
    "/pricing": "pricing",
    "/about": "about",
    "/contact": "contact",
    "/help/how-to-pay": "how-to-pay",
    "/install": "install",
    "/install-guide": "install",
    "/privacy": "privacy",
    "/terms": "terms",
    "/refund-policy": "refund-policy",
    "/publisher-agreement": "publisher-agreement",
    "/copyright": "copyright",
    "/toofi": "toofi",
    "/offline-study": "offline-study",
    "/trusted-resources": "trusted-resources",
    "/for-publishers": "for-publishers",
    "/share/study-plan": "study-plan-share",
  };

  return routes[pathname] ?? null;
};

const cleanParams = (params: PublicAttributionParams) =>
  Object.fromEntries(
    Object.entries(params).filter((entry): entry is [string, string] => typeof entry[1] === "string" && entry[1].trim().length > 0),
  );

export const appendQueryParams = (path: string, params: Record<string, string | undefined>) => {
  const url = new URL(path, "https://athr.local");
  Object.entries(params).forEach(([key, value]) => {
    if (typeof value === "string" && value.trim().length > 0) {
      url.searchParams.set(key, value);
    }
  });

  return `${url.pathname}${url.search}`;
};

export const buildPublicSignupPath = (params: PublicAttributionParams = {}) =>
  appendQueryParams("/auth/signup", cleanParams(params));

export const buildPublicLoginPath = (params: PublicAttributionParams = {}) =>
  appendQueryParams("/auth/login", cleanParams(params));

export const resolvePublicAppPath = (
  authenticated: boolean,
  accountType?: string | null,
  isAdmin = false,
  fallback = "/dashboard",
) => {
  if (!authenticated) return null;
  if (isAdmin) return "/admin";
  if (accountType === "library_owner") return "/publisher";
  return fallback;
};

export const pickPublicAttributionParams = (search: URLSearchParams): PublicAttributionParams => {
  const params: PublicAttributionParams = {};

  PUBLIC_ATTRIBUTION_KEYS.forEach((key) => {
    const value = search.get(key);
    if (value) {
      params[key] = value;
    }
  });

  return params;
};

export const persistPublicAttribution = (params: PublicAttributionParams) => {
  if (typeof window === "undefined") return;
  const cleaned = cleanParams(params);
  if (Object.keys(cleaned).length === 0) return;
  window.sessionStorage.setItem(PUBLIC_AUTH_ATTRIBUTION_STORAGE_KEY, JSON.stringify(cleaned));
};

export const getStoredPublicAttribution = (): PublicAttributionParams => {
  if (typeof window === "undefined") return {};

  try {
    const raw = window.sessionStorage.getItem(PUBLIC_AUTH_ATTRIBUTION_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as PublicAttributionParams;
    return cleanParams(parsed);
  } catch {
    return {};
  }
};

export const mergePublicAttribution = (...items: PublicAttributionParams[]) =>
  cleanParams(Object.assign({}, ...items));

export const buildAuthNextPath = (path: string, params: PublicAttributionParams = {}) =>
  buildAppPath(appendQueryParams(path, cleanParams(params)));
